import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Loader2,
  MapPin,
  Trash2,
  Users,
  Wallet,
  CircleOff,
} from "lucide-react";
import { API } from "service/Interceptor";
import {
  getCreatorBriefApplicants,
  getCreatorBriefs,
  isApplyComment,
  isCreatorBriefPost,
  mapPostToOpenBrief,
  removeCreatorBrief,
  upsertCreatorBrief,
  briefApplicantsPath,
  isApplicationsClosed,
  setBriefApplicationsClosed,
} from "utils/creatorBriefs";
import toast from "components/Toast/toast";
import BriefModal from "components/app/BriefModal";

const formatBudget = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 100000)
    return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000) return `₹${Math.round(num / 1000)}k`;
  return `₹${num}`;
};

const CreatorPostedBriefsPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useSelector((state) => state.userProfile);
  const creatorKey =
    userProfile?.userAliasId || userProfile?.id || userProfile?.userId;

  const [briefs, setBriefs] = useState([]);
  const [briefOpen, setBriefOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    const local = getCreatorBriefs(creatorKey);
    let fromApi = [];
    try {
      const res = await API.get("api/post", {
        params: {
          pageNo: 1,
          perPage: 40,
          sortBy: "latest",
          tags: "BRIEFS",
          postOwnerId: userProfile?.userAliasId || undefined,
        },
      });
      fromApi = (res?.data?.data?.results || [])
        .filter(isCreatorBriefPost)
        .map(mapPostToOpenBrief)
        .filter(Boolean);
      fromApi.forEach((b) => upsertCreatorBrief(creatorKey, b));
    } catch {
      /* keep local */
    }

    const byId = new Map();
    [...fromApi, ...local].forEach((b) => {
      if (b?.jobId && !byId.has(b.jobId)) byId.set(b.jobId, b);
    });
    const list = Array.from(byId.values()).sort(
      (a, b) =>
        new Date(b?.createdAt || 0).getTime() -
        new Date(a?.createdAt || 0).getTime()
    );
    setBriefs(list);

    const nextCounts = {};
    await Promise.all(
      list.map(async (brief) => {
        const localApps = [
          ...getCreatorBriefApplicants(brief.jobId),
          ...(brief.postId
            ? getCreatorBriefApplicants(`post-${brief.postId}`)
            : []),
        ];
        const postId =
          brief.postId ||
          (String(brief.jobId || "").startsWith("post-")
            ? String(brief.jobId).replace(/^post-/, "")
            : null);
        const jobUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          String(brief.jobId || "")
        )
          ? String(brief.jobId)
          : null;

        let jobTotal = 0;
        let commentTotal = 0;
        if (jobUuid) {
          try {
            const res = await API.get(`api/jobs/${jobUuid}/applicants`, {
              params: { page: 0, size: 1 },
              skipErrorToast: true,
            });
            const data = res?.data?.data;
            jobTotal = Number(
              data?.totalRecord ?? (data?.results || []).length ?? 0
            );
          } catch {
            jobTotal = 0;
          }
        }
        if (postId) {
          try {
            const res = await API.get(`api/post/${postId}/comment`, {
              skipErrorToast: true,
            });
            const comments = res?.data?.data?.results || [];
            commentTotal = Array.isArray(comments)
              ? comments.filter((c) =>
                  isApplyComment(c?.comment || c?.content || "")
                ).length
              : 0;
          } catch {
            commentTotal = 0;
          }
        }
        nextCounts[brief.jobId] = jobUuid
          ? Math.max(jobTotal, localApps.length)
          : Math.max(commentTotal, localApps.length);
      })
    );
    setCounts(nextCounts);
    setLoading(false);
  }, [creatorKey, userProfile?.userAliasId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const deleteBrief = async (project) => {
    if (!window.confirm("Remove this brief and its applicant list?")) return;
    try {
      const postId =
        project?.postId ||
        (String(project?.jobId || "").startsWith("post-")
          ? String(project.jobId).replace(/^post-/, "")
          : null);
      const rawJobId = project?.jobId;
      const isUuidJob =
        rawJobId &&
        !String(rawJobId).startsWith("post-") &&
        /^[0-9a-f-]{36}$/i.test(String(rawJobId));

      if (postId) {
        try {
          await API.delete(`api/post/${postId}`);
        } catch (_) {
          /* post may already be gone */
        }
      }
      if (isUuidJob) {
        try {
          await API.delete(`api/jobs/${rawJobId}`);
        } catch (_) {
          /* job may already be gone / ownership race */
        }
      }
      removeCreatorBrief(creatorKey, project.jobId);
      toast.success("Brief removed");
      refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to delete"
      );
    }
  };

  const toggleClosed = async (project) => {
    const closed = !isApplicationsClosed(project);
    try {
      await setBriefApplicationsClosed(project, closed);
      toast.success(closed ? "Applications closed" : "Applications reopened");
      refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not update brief"
      );
    }
  };

  return (
    <div className="applicants-page mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/projects"
        className="applicants-back inline-flex items-center gap-1.5 text-sm transition-colors mb-6"
      >
        <ArrowLeft size={15} /> Back to projects
      </Link>

      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#8b83ff] font-medium mb-2">
            Your posts
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter applicants-title">
            Posted briefs
          </h1>
          <p className="mt-2 applicants-subtitle max-w-2xl">
            Open a brief to review applicants with the same View details flow
            brands use.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setBriefOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-[#6C63F5] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#5751E5] transition-colors self-start"
        >
          <Briefcase size={16} /> Post a brief
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center applicants-muted flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading briefs…
        </div>
      ) : briefs.length === 0 ? (
        <div className="py-16 text-center rounded-2xl border border-dashed applicants-empty">
          <Users size={22} className="mx-auto mb-3 applicants-muted" />
          <p className="applicants-empty-title font-medium">No briefs yet</p>
          <p className="mt-1 text-sm applicants-muted">
            Post a collaboration call to start receiving applicants.
          </p>
          <button
            type="button"
            onClick={() => setBriefOpen(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#6C63F5] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#5751E5] transition-colors"
          >
            <Briefcase size={15} /> Post a brief
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {briefs.map((brief, i) => {
            const city =
              brief?.city?.name ||
              (typeof brief?.city === "string" ? brief.city : null);
            const budget = formatBudget(brief?.projectBudget ?? brief?.budget);
            const count = counts[brief.jobId] ?? brief?.applicantCount ?? 0;
            const industry =
              (Array.isArray(brief?.industry) && brief.industry[0]) ||
              brief?.category?.name ||
              null;

            return (
              <motion.div
                key={brief.jobId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i % 8) * 0.03 }}
                className="applicant-tile rounded-2xl border p-4 sm:p-5 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="applicant-tile-name font-display font-semibold truncate">
                      {brief.jobTitle || "Untitled brief"}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs applicant-tile-meta">
                      {industry && (
                        <span className="inline-flex items-center gap-1">
                          <Briefcase size={12} /> {industry}
                        </span>
                      )}
                      {city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} /> {city}
                        </span>
                      )}
                      {budget && (
                        <span className="inline-flex items-center gap-1">
                          <Wallet size={12} /> {budget}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Users size={12} /> {count}{" "}
                        {count === 1 ? "applicant" : "applicants"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(briefApplicantsPath(brief))
                      }
                      className="brief-owner-btn brief-owner-btn-primary rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
                    >
                      View applicants
                      <ArrowRight size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleClosed(brief)}
                      className="brief-owner-btn brief-owner-btn-edit rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
                    >
                      <CircleOff size={13} />
                      {isApplicationsClosed(brief) ? "Reopen" : "Close applications"}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteBrief(brief)}
                      className="applicant-action applicant-action-reject rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors inline-flex items-center gap-1.5"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <BriefModal
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        onPosted={() => {
          setBriefOpen(false);
          refresh();
        }}
      />
    </div>
  );
};

export default CreatorPostedBriefsPage;
